import { ruleFields, ruleTriggerDescription, durationHumanize, getTemperatureUnitSuffix, getDifferentialPressureUnitSuffix, getHoursMinutesFormat } from 'services/utils';
import { cloneDeep as _cloneDeep, isEqual as _isEqual } from 'lodash';
import { prepareTemplateForSave as prepareForSave } from 'services/templateSaveUtils';
import { States } from '../../../../app.router';
import { findIncompatiblePlaceholders } from 'services/AlertTemplateHelper';
import { getAvailableDynamicInputs } from 'services/alerts/dynamicInputs';
import { Duration } from '../../../../modules/alerts/rules/rules-detail/duration/duration.controller';
import moment from 'moment';
import angular from 'angular';
import { buildContactGroupInfo } from '../contact-group-utils';
import {
    getDeviceTypesForTrigger,
    projectHasSensors,
    projectHasTimezone
} from '../template-projects-utils';

const locationModalTemplate = require('./location-modal/location-modal.html');
const LocationModalController = require('./location-modal/location-modal.controller').default;

const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default class TemplateDetailController {

    /* @ngInject */
    constructor($scope, DialogService, $timeout, FeatureFlags, ProjectManager, IAMService, $state, SensorService, ToastService) {
        this.$scope = $scope;
        this.DialogService = DialogService;
        this.$timeout = $timeout;
        this.FeatureFlags = FeatureFlags;
        this.ProjectManager = ProjectManager;
        this.$state = $state;
        this.SensorService = SensorService;
        this.IAMService = IAMService;
        this.ToastService = ToastService;
        this.computedWarnings = {};

        this.fields = ruleFields;
        this.dayOptions = dayOptions;
        this.lastActionFocus = null;
        this.isNew = true;

        // Schedule-related properties
        this.scheduleMode = 'EVERYDAY';
        this.activeScheduleDays = [];
        this.timeOptions = [];
        this.scheduleStartIndex = 0;
        this.scheduleEndIndex = 0;
        this.customScheduleIndexes = [];
        this.defaultScheduleStartTime = '8,0';
        this.defaultScheduleEndTime = '17,0';

        // User and project timezone
        this.userTimezone = null;
        if (typeof Intl !== "undefined") {
            this.userTimezone = Intl.DateTimeFormat?.().resolvedOptions().timeZone;
        }
        this.projectTimezone = this.ProjectManager?.currentProject?.location?.timeLocation || '';
        this.scheduleTimezoneOption = null;

        // Webhook URL regex pattern
        this.webhookUrlRegex = /^https:\/\/.+/;

        // Email validation regex pattern
        this.emailRegex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;

        // Phone number regex patterns
        this.phoneRegex = /^\+[1-9]\d{9,14}$/;
        this.phoneCallRegex = /^\+[1-9]\d{9,14}(,(,|[0-9]|#|\*){1,19})?$/;

        // Available dynamic inputs for actions (populated by updateDynamicInputs)
        this.availableDynamicInputs = [];

        // Initialize acknowledgment duration
        this.acknowledgmentDuration = {
            hours: 4,
            minutes: 0
        };
        this.invalidAcknowledgmentDuration = false;

        // Template selectors
        this.selectedEscalationTemplate = null;
        this.showEscalationTemplateSelector = false;

        // Placeholder compatibility warnings
        this.placeholderWarnings = [];

        // Trigger mode settings
        this.triggerMode = 'immediately';
        this.showAdvancedOptions = false;

        // Virtual Glycol Buffer support
        this.glycolFilterEnabled = false;
    }

    $onInit() {
        // Build time options for schedule
        this.timeOptions = [];
        for (let hour = 0; hour < 24; hour++) {
            this.timeOptions.push({
                value: `${hour},${0}`,
                displayValue: moment({ hour }).format(getHoursMinutesFormat())
            })
            this.timeOptions.push({
                value: `${hour},${30}`,
                displayValue: moment({ hour, minute: 30 }).format(getHoursMinutesFormat())
            })
        }
        this.timeOptions.push({
            value: `${23},${59}`,
            displayValue: moment({ hour: 23, minute: 59 }).format(getHoursMinutesFormat())
        })

        // Initialize the template data
        this.initializeTemplateData();

        this.matchingProjectsCount = 0;
        this.totalProjectsCount = 0;
        this.matchingProjects = [];
        this.nonMatchingProjects = [];
        this.hasProjectLabels = false;
        this.showProjectDetails = false;
        this.isLoadingProjectsCount = false;
        this.projectsWithoutTimezone = [];
        this.isCheckingTimezones = false;
        this.timezoneCheckGeneration = 0;

        // Always fetch projects information for organization templates
        this.fetchMatchingProjectsCount();

        // Fetch available label keys for autocomplete
        this.SensorService.listLabelKeys().then(response => {
            // Extract device label keys for device section (filter out reserved keys)
            this.availableDeviceLabelKeys = [];
            if (response.deviceLabelKeys) {
                this.availableDeviceLabelKeys.push(...response.deviceLabelKeys
                    .filter(item => item.key !== 'name' && item.key !== 'description')
                    .map(item => ({
                        key: item.key,
                        deviceCount: item.deviceCount,
                        type: 'device'
                    })));
            }
            // Sort by device count (descending) to show most used labels first
            this.availableDeviceLabelKeys.sort((a, b) => b.deviceCount - a.deviceCount);

            // Extract project label keys for project section
            this.availableProjectLabelKeys = [];
            if (response.projectLabelKeys) {
                this.availableProjectLabelKeys.push(...response.projectLabelKeys.map(item => ({
                    key: item.key,
                    deviceCount: item.deviceCount,
                    type: 'project'
                })));
            }
            // Sort by device count (descending) to show most used labels first
            this.availableProjectLabelKeys.sort((a, b) => b.deviceCount - a.deviceCount);

            // Keep the old property for backward compatibility with device section
            this.availableLabelKeys = this.availableDeviceLabelKeys;
        }).catch(error => {
            console.error('Error fetching label keys:', error); // eslint-disable-line no-console
            this.availableDeviceLabelKeys = [];
            this.availableProjectLabelKeys = [];
            this.availableLabelKeys = [];
        });
    }

    $onChanges(changes) {
        // If the template binding changes, reinitialize the template data
        if (changes.template && !changes.template.isFirstChange()) {
            this.initializeTemplateData();
        }
        if (changes.contactGroupInfo) {
            this.recomputeAllWarnings();
        }
    }

    initializeTemplateData() {
        // Use the isNew binding from parent component
        // this.isNew is set via component bindings

        // Apply the same conversions as project rules to ensure consistency
        if (!this.isNew && this.template) {
            // Convert field name for count based notifications (locally in Studio)
            if (this.template.trigger?.field === 'objectPresent' && this.template.trigger?.triggerCount) {
                this.template.trigger.field = 'proximityCount';
            }
            if (this.template.trigger?.field === 'touch' && this.template.trigger?.triggerCount) {
                this.template.trigger.field = 'touchCount';
            }
            if (this.template.trigger?.field === 'contact' && this.template.trigger?.triggerCount) {
                this.template.trigger.field = 'contactCount';
            }

            // BELOW and ABOVE range.type are added on the frontend as a convenience
            // These are not supported on the backend, OUTSIDE is the type that is actually sent on the API
            if (this.template.trigger?.range?.type === 'OUTSIDE') {
                if (this.template.trigger.range.lower !== null && this.template.trigger.range.upper === null) {
                    this.template.trigger.range.type = 'BELOW';
                }
                if (this.template.trigger.range.lower === null && this.template.trigger.range.upper !== null) {
                    this.template.trigger.range.type = 'ABOVE';
                }
            }
        }

        if (this.isNew) {
            // For new templates, ensure we have proper structure
            if (!this.template) {
                this.template = {};
            }

            // Set up template with proper defaults for new templates
            this.template.displayName = this.template.displayName || '';
            this.template.enabled = this.template.enabled !== undefined ? this.template.enabled : true;
            this.template.trigger = this.template.trigger || { field: null };
            this.template.scope = this.template.scope || { type: 'ALL_PROJECTS', labels: {} };
            this.template.deviceMode = this.template.deviceMode || 'ALL'; // Default to all devices for new templates
            this.template.deviceLabels = this.template.deviceLabels || {};
            this.template.escalationLevels = this.template.escalationLevels || [];
            this.template.actions = this.template.actions || [];
        } else {
            // For existing templates, ensure proper structure exists and convert from backend format

            if (!this.template.scope) {
                this.template.scope = {
                    type: 'ALL_PROJECTS',
                    labels: {}
                };
            }
            if (!this.template.scope.labels) {
                this.template.scope.labels = {};
            }

            // Ensure contact groups arrays exist for all actions
            if (this.template.escalationLevels) {
                this.template.escalationLevels.forEach(escalation => {
                    if (escalation.actions) {
                        escalation.actions.forEach(action => {
                            const actionObject = action[action.type.toLowerCase()];
                            if (actionObject && (action.type === 'EMAIL' || action.type === 'SMS' || action.type === 'PHONE_CALL')) {
                                if (!actionObject.contactGroups) {
                                    actionObject.contactGroups = [];
                                }
                            }
                        });
                    }
                });
            }

            // Convert old array format to object format for backward compatibility
            if (Array.isArray(this.template.scope.labels)) {
                const labelsObject = {};
                this.template.scope.labels.forEach(label => {
                    if (label.key) {
                        labelsObject[label.key] = label.value || '';
                    }
                });
                this.template.scope.labels = labelsObject;
            }

            // Determine what deviceMode the template is set to (like project rules)
            if (this.template.deviceLabels && Object.keys(this.template.deviceLabels).length > 0) {
                this.template.deviceMode = 'LABELS';
            } else {
                this.template.deviceMode = 'ALL';
            }

            // Ensure device selection properties exist
            if (!this.template.deviceLabels) {
                this.template.deviceLabels = {};
            }

            // Ensure escalateAfter values are strings (for backward compatibility)
            if (this.template.escalationLevels) {
                this.template.escalationLevels.forEach(level => {
                    if (typeof level.escalateAfter === 'number') {
                        level.escalateAfter += 's';
                    }
                });
            }
        }

        // Initialize acknowledgment duration from template data
        this.invalidAcknowledgmentDuration = false;
        if (this.template.unacknowledgesAfter === null || this.template.unacknowledgesAfter === undefined || this.isNew) {
            // Set the default value
            this.acknowledgmentDuration = {
                hours: 4,
                minutes: 0,
            };
        } else {
            // Existing template with a set value
            const duration = Duration.fromString(this.template.unacknowledgesAfter);
            this.acknowledgmentDuration = {
                hours: duration.hours,
                minutes: duration.minutes,
            };
        }

        // Handle schedule initialization (like project rules)
        const schedule = this.template.schedule;
        if (schedule) {
            this.activeScheduleDays = [];

            // Convert time objects to string for use as ng-model for input selection
            // E.g. {hour: 8, minute: 0} is reduced to '8,0'
            // Converted back when saving notification
            schedule.slots.forEach(slot => {
                slot.times.forEach(time => {
                    time.start = `${time.start.hour},${time.start.minute}`
                    time.end = `${time.end.hour},${time.end.minute}`
                });
            });

            this.defaultScheduleStartTime = '8,0';
            this.defaultScheduleEndTime = '17,0';

            // Determine the current scheduleMode, EVERYDAY, WEEKDAYS or CUSTOM
            if (schedule.slots.length === 1 && schedule.slots[0]?.days.length >= 5) {
                const days = schedule.slots[0].days;
                this.activeScheduleDays = days;

                if (days.length === 7) {
                    this.scheduleMode = 'EVERYDAY'
                }

                if (days.length === 5 && !days.includes('Saturday') && !days.includes('Sunday')) {
                    this.scheduleMode = 'WEEKDAYS'
                }

                // Helping variables for EVERYDAY and WEEKDAYS schedule time options
                this.scheduleStartIndex = this.timeOptions.map(option => option.value).indexOf(schedule.slots[0].times[0].start);
                this.scheduleEndIndex = this.timeOptions.map(option => option.value).indexOf(schedule.slots[0].times[0].end);

            } else {

                this.scheduleMode = 'CUSTOM'
                // Helping array for all CUSTOM schedule time options
                // One element for each day
                this.customScheduleIndexes = [];
                this.dayOptions.forEach(() => {
                    this.customScheduleIndexes.push({ start: this.scheduleStartIndex, end: this.scheduleEndIndex })
                })
                schedule.slots.forEach(slot => {
                    this.activeScheduleDays.push(...slot.days)
                    const startIndex = this.timeOptions.map(option => option.value).indexOf(slot.times[0].start);
                    const endIndex = this.timeOptions.map(option => option.value).indexOf(slot.times[0].end);

                    const dayIndex = this.dayOptions.indexOf(slot.days[0]);
                    this.customScheduleIndexes[dayIndex].start = startIndex;
                    this.customScheduleIndexes[dayIndex].end = endIndex;
                });
            }

            // Initialize the timezone option based on current schedule settings
            this.initializeScheduleTimezoneOption();
        } else {
            // Initialize timezone option to null when no schedule exists
            this.scheduleTimezoneOption = null;
        }

        // Initialize device-related properties
        this.devicesMatchingLabels = [];

        // Check devices matching labels if label mode is selected
        if (this.template.deviceMode === 'LABELS') {
            this.checkDevicesMatchingLabels();
        }

        // Assign fields directly as object (not array)
        this.fields = ruleFields;



        // Initialize trigger mode based on whether template has a delay
        if (this.template.triggerDelay) {
            this.triggerMode = 'delay';
        } else {
            this.triggerMode = 'immediately';
        }
        this.showAdvancedOptions = false;
        this.minTriggerDelay = 1; // 1 minute minimum

        // Set correct minTriggerDelay for pre-configured connection status templates
        if (this.template.trigger?.field === 'connectionStatus' && this.template.trigger?.connection === 'SENSOR_OFFLINE') {
            this.minTriggerDelay = 15;
        }

        // Initialize Virtual Glycol Buffer based on template data
        this.glycolFilterEnabled = false;

        if (this.template.trigger?.range?.filter && this.template.trigger.range.filter.productEquivalentTemperature) {
            this.glycolFilterEnabled = true;
        }


        // Show advanced options if any advanced features are enabled
        this.showAdvancedOptions = this.glycolFilterEnabled || this.template.reminderNotifications || this.template.resolvedNotifications;

        // Update dynamic inputs based on trigger type
        this.updateDynamicInputs();

        // Store original template for change detection (after all setup is complete)
        this.originalTemplate = _cloneDeep(this.template);

        // initialize the min jolt time to complete
        this.minJoltTimeToComplete = 59; // ~1 minute
        this.maxJoltTimeToComplete = 604800; // 7 days
    }





    // Scope management
    setScopeType(type) {
        this.template.scope.type = type;
        if (type !== 'PROJECT_LABELS') {
            this.template.scope.labels = {};
        }
        this.fetchMatchingProjectsCount();
    }

    // Project scope label methods (using same pattern as device labels)
    addProjectLabel({ label }) {
        if (!this.template.scope.labels) {
            this.template.scope.labels = {};
        }
        this.template.scope.labels[label.key] = label.value;
        this.fetchMatchingProjectsCount();
        return Promise.resolve();
    }

    updateProjectLabel(label) {
        this.template.scope.labels[label.labelKey] = label.value;
        this.fetchMatchingProjectsCount();
        return Promise.resolve();
    }

    removeProjectLabel(label) {
        delete this.template.scope.labels[label.labelKey];
        this.fetchMatchingProjectsCount();
        return Promise.resolve();
    }

    // Device selection management
    setIncludedDeviceMode(mode) {
        this.template.deviceMode = mode;

        // Reset all values if mode is changed
        this.template.deviceLabels = {};
        this.devicesMatchingLabels = [];

        if (this.template.deviceMode === 'LABELS') {
            this.checkDevicesMatchingLabels();
        }
    }

    // Device labels management
    checkDevicesMatchingLabels() {
        if (!this.template.deviceLabels || Object.keys(this.template.deviceLabels).length === 0) {
            this.devicesMatchingLabels = [];
            return;
        }

        // For templates, we can't check against a specific project's devices
        // This would need to be handled at template application time
        this.devicesMatchingLabels = [];
    }

    addDeviceLabel({ label }) {
        if (!this.template.deviceLabels) {
            this.template.deviceLabels = {};
        }
        this.template.deviceLabels[label.key] = label.value;
        this.checkDevicesMatchingLabels();
        return Promise.resolve();
    }

    updateDeviceLabel(label) {
        this.template.deviceLabels[label.labelKey] = label.value;
        this.checkDevicesMatchingLabels();
        return Promise.resolve();
    }

    removeDeviceLabel(label) {
        delete this.template.deviceLabels[label.labelKey];
        this.checkDevicesMatchingLabels();
        return Promise.resolve();
    }

    // Callback from threshold-builder when trigger field changes
    onTriggerFieldChange() {
        this.updateDynamicInputs();
        this.checkProjectsWithoutTimezone();
        this.checkEscalationTemplateCompatibility();
        this.checkPlaceholderCompatibility();
    }

    // Callback from escalation-builder when levels change (manual mode only)
    onEscalationLevelsChange(levels) {
        this.template.escalationLevels = levels;
        this.checkPlaceholderCompatibility();
    }

    // Check if the referenced escalation template is still compatible with the trigger
    checkEscalationTemplateCompatibility() {
        if (!this.template.escalationTemplateId) return;

        const triggerField = this.template.trigger?.field;
        if (!triggerField) return; // No trigger selected yet — skip check

        const tmpl = (this.escalationTemplates || []).find(t => t.name === this.template.escalationTemplateId);
        if (!tmpl) return;

        if (tmpl.triggerType && tmpl.triggerType !== triggerField) {
            // Incompatible — auto-clear
            this.template.escalationTemplateId = null;
            this.template.escalationLevels = [];
            this.showEscalationTemplateSelector = true;
            this.selectedEscalationTemplate = null;
        }
    }

    // Resolve escalation levels from template reference or manual config
    getResolvedEscalationLevels() {
        if (this.template.escalationTemplateId) {
            const tmpl = (this.escalationTemplates || []).find(t => t.name === this.template.escalationTemplateId);
            if (tmpl) return tmpl.escalationLevels;
        }
        return this.template.escalationLevels || [];
    }

    get isUsingEscalationTemplate() {
        return !!this.template.escalationTemplateId;
    }

    getReferencedEscalationTemplateName() {
        if (!this.template.escalationTemplateId) return '';
        const tmpl = (this.escalationTemplates || []).find(t => t.name === this.template.escalationTemplateId);
        return tmpl ? tmpl.displayName : '';
    }

    // --- Placeholder compatibility ---

    checkPlaceholderCompatibility() {
        this.placeholderWarnings = [];
        const levels = this.getResolvedEscalationLevels();
        if (!this.template.trigger?.field || !levels) return;

        levels.forEach((level, levelIndex) => {
            if (!level.actions) return;
            level.actions.forEach((action, actionIndex) => {
                const texts = this.getActionTexts(action);
                texts.forEach(({ fieldName, text }) => {
                    const incompatible = findIncompatiblePlaceholders(text, this.template.trigger.field);
                    incompatible.forEach(({ placeholder }) => {
                        this.placeholderWarnings.push({
                            levelIndex,
                            actionIndex,
                            actionType: action.type,
                            fieldName,
                            placeholder,
                        });
                    });
                });
            });
        });
    }

    getActionTexts(action) { // eslint-disable-line class-methods-use-this
        const texts = [];
        if (action.sms) texts.push({ fieldName: 'body', text: action.sms.body });
        if (action.email) {
            texts.push({ fieldName: 'subject', text: action.email.subject });
            texts.push({ fieldName: 'body', text: action.email.body });
        }
        if (action.phoneCall) {
            texts.push({ fieldName: 'introduction', text: action.phoneCall.introduction });
            texts.push({ fieldName: 'message', text: action.phoneCall.message });
        }
        return texts;
    }

    // --- Template selectors ---

    availableEscalationsForTrigger() {
        if (!this.escalationTemplates) return [];
        const triggerField = this.template?.trigger?.field;
        if (!triggerField) return this.escalationTemplates;

        return this.escalationTemplates.filter(t =>
            !t.triggerType || t.triggerType === triggerField
        );
    }

    applyEscalationTemplate() {
        if (!this.selectedEscalationTemplate) {
            // "Configure manually..." selected — detach if referenced
            if (this.template.escalationTemplateId) {
                this.detachEscalationTemplate();
            }
            return;
        }
        const e = this.selectedEscalationTemplate;
        this.template.escalationTemplateId = e.name;
        this.template.escalationLevels = []; // Clear manual levels — reference takes over
        this.showEscalationTemplateSelector = false;
        this.selectedEscalationTemplate = null;
        this.checkPlaceholderCompatibility();
        this.$scope.$applyAsync();
    }

    detachEscalationTemplate() {
        if (!this.template.escalationTemplateId) return;
        // Copy levels from referenced template into the alert template
        const tmpl = (this.escalationTemplates || []).find(t => t.name === this.template.escalationTemplateId);
        if (tmpl && tmpl.escalationLevels) {
            this.template.escalationLevels = JSON.parse(JSON.stringify(tmpl.escalationLevels));
        }
        this.template.escalationTemplateId = null;
        this.selectedEscalationTemplate = null;
        this.$scope.$applyAsync();
    }

    removeEscalationTemplate() {
        this.template.escalationTemplateId = null;
        this.template.escalationLevels = [];
        this.selectedEscalationTemplate = null;
        this.showEscalationTemplateSelector = false;
        this.placeholderWarnings = [];
        this.$scope.$applyAsync();
    }

    // Trigger configuration (legacy — now handled by threshold-builder)
    triggerFieldChanged() {
        // Clear existing trigger properties except field
        const field = this.template.trigger.field;
        this.template.trigger = { field };

        // Initialize specific trigger properties based on operator type
        const operator = this.getOperatorByField(field);

        if (operator === 'range') {
            // Initialize range object
            this.template.trigger.range = {
                type: 'ABOVE',
                lower: null,
                upper: null
            };
        } else if (operator === 'count') {
            // Initialize trigger count for count-based triggers
            this.template.trigger.triggerCount = 2;
        }

        // Set defaults from field configuration AFTER initializing base structure
        if (this.fields[field]?.default) {
            Object.assign(this.template.trigger, this.fields[field].default);
        }

        // Handle connection status
        if (field === 'connectionStatus') {
            this.connectionStatusTypeChanged();
        }

        // Reset advanced options and trigger mode
        this.showAdvancedOptions = false;
        this.template.reminderNotifications = false;
        this.template.resolvedNotifications = false;
        this.glycolFilterEnabled = false;
        this.triggerMode = 'immediately';
        this.template.triggerDelay = null;

        // Update dynamic inputs
        this.updateDynamicInputs();

        // Re-check timezone warning for new device types
        this.checkProjectsWithoutTimezone();
    }

    connectionStatusTypeChanged() {
        if (this.template.trigger.connection === 'SENSOR_OFFLINE') {
            // Force delay mode for sensor offline
            this.triggerMode = 'delay';
            if (!this.template.triggerDelay || parseInt(this.template.triggerDelay, 10) < 15 * 60) {
                this.template.triggerDelay = `${15 * 60}s`; // 15 minutes minimum
            }
            this.minTriggerDelay = 15; // 15 minutes minimum for sensor offline
        } else {
            this.minTriggerDelay = 1; // 1 minute minimum for other triggers
            // Reset trigger mode based on whether there's still a delay configured
            if (this.template.triggerDelay) {
                this.triggerMode = 'delay';
            } else {
                this.triggerMode = 'immediately';
            }
        }
        this.checkProjectsWithoutTimezone();
    }

    getDisplayOption(value) {
        return this.fields[this.template.trigger.field]?.operatorDisplayNames?.[value] || value;
    }

    getCountStatusHelperText() {
        const field = this.template.trigger.field;
        if (field === 'touchCount') {
            return 'touches';
        }
        if (field === 'proximityCount') {
            const presence = this.template.trigger.presence;
            if (presence === 'PRESENT') {
                return 'times object becomes present';
            }
            return 'times object becomes not present';
        }
        if (field === 'contactCount') {
            const contact = this.template.trigger.contact;
            if (contact === 'OPEN') {
                return 'times door/window opens';
            }
            return 'times door/window closes';
        }
        return 'events';
    }



    getOperatorByField(field) {
        return this.fields[field]?.operator;
    }

    getUnitByField(field) {
        // Show the correct fahrenheit/celsius unit if temperature, and the default unit otherwise
        if (field === "temperature") {
            return getTemperatureUnitSuffix();
        }
        if (field === 'differentialPressure') {
            return getDifferentialPressureUnitSuffix();
        }

        const fieldConfig = this.fields[field];
        return fieldConfig?.unit || '';
    }

    triggerRangeTypeChanged() {
        // Clear range values when type changes
        if (this.template.trigger.range.type === 'ABOVE') {
            this.template.trigger.range.lower = null;
        } else if (this.template.trigger.range.type === 'BELOW') {
            this.template.trigger.range.upper = null;
        }
    }

    // Trigger delay management
    triggerModeChanged() {
        if (this.triggerMode === 'immediately') {
            this.template.triggerDelay = null;
        } else {
            // Set default delay
            this.template.triggerDelay = `${this.minTriggerDelay * 30 * 60}s`; // Convert to seconds with 's' suffix
        }
    }

    // Virtual Glycol Buffer management
    glycolFilterChanged() {
        if (this.glycolFilterEnabled) {
            if (!this.template.trigger.range) {
                this.template.trigger.range = {};
            }
            this.template.trigger.range.filter = {
                productEquivalentTemperature: {}
            };
        } else if (this.template.trigger.range) {
            this.template.trigger.range.filter = null;
        }
    }

    acknowledgmentDurationChanged() {
        // Convert hours and minutes to seconds on string format e.g. '7200s'
        const duration = new Duration(0, this.acknowledgmentDuration.minutes, this.acknowledgmentDuration.hours);
        this.invalidAcknowledgmentDuration = duration.momentDuration.asSeconds() < 60 || duration.momentDuration.asSeconds() > 604800;
        if (!this.invalidAcknowledgmentDuration) {
            this.template.unacknowledgesAfter = duration.toString();
        }
    }

    // Schedule management methods
    setStartIndex(day, time) {
        // Selected start index for schedule time options
        if (this.scheduleMode !== 'CUSTOM') {
            this.scheduleStartIndex = this.timeOptions.map(e => e.value).indexOf(time);
        } else {
            const dayIndex = this.dayOptions.indexOf(day);
            this.customScheduleIndexes[dayIndex].start = this.timeOptions.map(e => e.value).indexOf(time);
        }
    }

    setEndIndex(day, time) {
        // Selected end index for schedule time options
        if (this.scheduleMode !== 'CUSTOM') {
            this.scheduleEndIndex = this.timeOptions.map(e => e.value).indexOf(time);
        } else {
            const dayIndex = this.dayOptions.indexOf(day);
            this.customScheduleIndexes[dayIndex].end = this.timeOptions.map(e => e.value).indexOf(time);
        }
    }

    scheduleDayToggle(day) {
        const index = this.activeScheduleDays.indexOf(day);
        if (index !== -1) {
            // Remove schedule slot for give day
            this.activeScheduleDays.splice(index, 1);
            const slotIndex = this.template.schedule.slots.map(slot => slot.days.includes(day)).indexOf(true);
            this.template.schedule.slots.splice(slotIndex, 1);
        } else {
            // Add new slot for given day
            this.activeScheduleDays.push(day)
            this.template.schedule.slots.push({
                days: [day],
                times: [{
                    start: this.defaultScheduleStartTime,
                    end: this.defaultScheduleEndTime
                }]
            })
        }
    }

    enableSchedule() {
        this.activeScheduleDays = this.dayOptions;

        this.scheduleTimezoneOption = 'use-project-timezone'; // Set to use project timezone by default

        this.template.schedule = {
            inverse: false,
            timezone: '',
            useProjectTimeLocation: true
        };
        this.scheduleMode = 'EVERYDAY';
        this.scheduleModeChanged();
        this.checkProjectsWithoutTimezone();
    }

    removeSchedule() {
        delete this.template.schedule;
        this.projectsWithoutTimezone = [];
    }

    initializeScheduleTimezoneOption() {
        if (this.template.schedule.useProjectTimeLocation) {
            this.scheduleTimezoneOption = 'use-project-timezone';
        } else if (this.template.schedule.timezone) {
            this.scheduleTimezoneOption = this.template.schedule.timezone;
        } else {
            // No timezone selected - show placeholder
            this.scheduleTimezoneOption = null;
        }
    }

    scheduleTimezoneOptionChanged() {
        if (!this.scheduleTimezoneOption || this.scheduleTimezoneOption === '') {
            // No timezone selected yet
            this.template.schedule.useProjectTimeLocation = false;
            this.template.schedule.timezone = null;
        } else if (this.scheduleTimezoneOption === 'use-project-timezone') {
            this.template.schedule.useProjectTimeLocation = true;
            this.template.schedule.timezone = '';
        } else {
            this.template.schedule.useProjectTimeLocation = false;
            this.template.schedule.timezone = this.scheduleTimezoneOption;
        }
        this.checkProjectsWithoutTimezone();
    }

    checkProjectsWithoutTimezone() {
        if (!this.hasScheduleWithProjectTimezone()) {
            this.projectsWithoutTimezone = [];
            this.isCheckingTimezones = false;
            return;
        }

        const candidateProjects = this.getProjectsToCheck().filter(project => {
            return projectHasSensors(project) && !projectHasTimezone(project);
        });

        if (candidateProjects.length === 0) {
            this.projectsWithoutTimezone = [];
            this.isCheckingTimezones = false;
            return;
        }

        const deviceTypes = getDeviceTypesForTrigger(this.template);
        if (deviceTypes.length === 0) {
            this.projectsWithoutTimezone = candidateProjects;
            this.isCheckingTimezones = false;
            return;
        }

        this.isCheckingTimezones = true;
        this.filterProjectsByRelevantDeviceTypes(candidateProjects, deviceTypes);
    }

    filterProjectsByRelevantDeviceTypes(candidateProjects, deviceTypes) {
        const generation = ++this.timezoneCheckGeneration;
        const checks = candidateProjects.map(project => {
            const projectId = project.name.split('/')[1];
            return this.SensorService.sensors({ deviceTypes, pageSize: 1 }, projectId)
                .then(response => (response.data && response.data.length > 0) ? project : null)
                .catch(() => null);
        });

        Promise.all(checks).then(results => {
            if (generation !== this.timezoneCheckGeneration) return;
            this.projectsWithoutTimezone = results.filter(p => p !== null);
            this.isCheckingTimezones = false;
            this.$scope.$applyAsync();
        });
    }

    openLocationModal() {
        this.DialogService.show({
            template: locationModalTemplate,
            controller: LocationModalController,
            controllerAs: '$ctrl',
            parent: document.body,
            clickOutsideToClose: false,
            escapeToClose: true,
            fullscreen: false,
            locals: {
                projects: this.projectsWithoutTimezone,
                allProjects: this.allProjects || []
            }
        }).then(() => {
            this.fetchMatchingProjectsCount();
        }).catch(() => {
            // Dialog cancelled — still refresh in case locations were updated before closing
            this.fetchMatchingProjectsCount();
        });
    }

    hasScheduleWithProjectTimezone() {
        return this.template.schedule && this.template.schedule.useProjectTimeLocation;
    }

    getProjectsToCheck() {
        if (this.template.scope.type === 'PROJECT_LABELS' && this.matchingProjects.length > 0) {
            return this.matchingProjects;
        }
        return this.allProjects || [];
    }

    scheduleModeChanged() {
        // Reset schedule slots and helping variables
        this.template.schedule.slots = [];
        this.defaultScheduleStartTime = '8,0';
        this.defaultScheduleEndTime = '17,0';
        this.scheduleStartIndex = this.timeOptions.map(e => e.value).indexOf(this.defaultScheduleStartTime);
        this.scheduleEndIndex = this.timeOptions.map(e => e.value).indexOf(this.defaultScheduleEndTime);

        // Set new mode with default values
        switch (this.scheduleMode) {
            case 'EVERYDAY':
                this.activeScheduleDays = this.dayOptions.slice(0);
                this.template.schedule.slots = [{
                    days: this.activeScheduleDays,
                    times: [{
                        start: this.defaultScheduleStartTime,
                        end: this.defaultScheduleEndTime
                    }]
                }]
                break;

            case 'WEEKDAYS':
                this.activeScheduleDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
                this.template.schedule.slots = [{
                    days: this.activeScheduleDays,
                    times: [{
                        start: this.defaultScheduleStartTime,
                        end: this.defaultScheduleEndTime
                    }]
                }]
                break;

            case 'CUSTOM':
                this.activeScheduleDays = this.dayOptions.slice(0);
                this.customScheduleIndexes = [];
                this.dayOptions.forEach(day => {
                    this.template.schedule.slots.push({
                        days: [day],
                        times: [{
                            start: this.defaultScheduleStartTime,
                            end: this.defaultScheduleEndTime
                        }]
                    })
                    this.customScheduleIndexes.push({ start: this.scheduleStartIndex, end: this.scheduleEndIndex });
                });
                break;

            default:
                console.error(`Unknown Schedule mode: ${this.scheduleMode}`) // eslint-disable-line no-console
        }
    }

    // Action management
    addAction(escalation, actionType) {
        let targetEscalation = escalation;
        if (!targetEscalation) {
            // Create first escalation level if none exists
            if (this.template.escalationLevels.length === 0) {
                this.addEscalationLevel();
                targetEscalation = this.template.escalationLevels[0];
            } else {
                targetEscalation = this.template.escalationLevels[0];
            }
        }

        const action = this.createActionByType(actionType);
        targetEscalation.actions.push(action);
    }

    createActionByType(type) {
        const baseAction = { type };

        switch (type) {
            case 'EMAIL':
                return {
                    ...baseAction,
                    email: {
                        recipients: [],
                        contactGroups: [],
                        subject: '',
                        body: ''
                    }
                };
            case 'SMS':
                return {
                    ...baseAction,
                    sms: {
                        recipients: [],
                        contactGroups: [],
                        body: ''
                    }
                };
            case 'PHONE_CALL':
                return {
                    ...baseAction,
                    phoneCall: {
                        recipients: [],
                        contactGroups: [],
                        introduction: 'This is an automated call from Disruptive Technologies.',
                        message: ''
                    }
                };
            case 'WEBHOOK':
                return {
                    ...baseAction,
                    webhook: {
                        url: '',
                        headers: {}
                    }
                };
            case 'CORRIGO':
                return {
                    ...baseAction,
                    corrigo: {
                        assetId: '',
                        taskId: '',
                        customerId: '',
                        clientId: '',
                        clientSecret: '',
                        companyName: '',
                        subTypeId: '',
                        contactName: '',
                        contactAddress: '',
                        workOrderDescription: this.defaultCorrigoWorkOrderDescription
                    }
                };
            case 'SERVICE_CHANNEL':
                return {
                    ...baseAction,
                    serviceChannel: {
                        storeId: '',
                        assetTagId: '',
                        trade: '',
                        description: ''
                    }
                };
            case 'JOLT_LIST':
                return {
                    ...baseAction,
                    joltList: {
                        displayName: '',
                        listTemplateId: '',
                        locationId: '',
                        timeToComplete: '',
                        userName: '',
                        password: '',
                        appId: '',
                        companyId: '',
                    }
                };
            default:
                return baseAction;
        }
    }

    removeAction(escalation, actionIndex) {
        escalation.actions.splice(actionIndex, 1);
    }

    hasActions(escalation) {
        return escalation && escalation.actions && escalation.actions.length > 0;
    }

    // Escalation level management
    addEscalationLevel() {
        const escalationLevel = {
            displayName: `Level ${this.template.escalationLevels.length + 1}`,
            actions: [],
            escalateAfter: '7200s' // 2 hours as duration string
        };
        this.template.escalationLevels.push(escalationLevel);
    }

    removeEscalationLevel(index) {
        this.template.escalationLevels.splice(index, 1);
    }

    escalationDescription(escalation) {
        const index = this.template.escalationLevels.indexOf(escalation);
        if (index === 0) {
            // For the first escalation level, show the trigger description
            if (this.template.trigger && this.template.trigger.field) {
                return `Will be sent when ${ruleTriggerDescription(this.template)}`;
            }
            return 'Will be sent when trigger condition is met';
        }

        // For subsequent levels, show the delay from previous level
        const previousEscalation = this.template.escalationLevels[index - 1];
        const humanizedDelay = durationHumanize(previousEscalation.escalateAfter);
        return `Will be sent ${humanizedDelay} after the previous escalation if the alert remains unacknowledged, unaddressed, or unresolved.`;
    }



    // Dynamic input insertion
    insertDynamicInput(actionObject, placeholder) {
        const field = this.getFieldForLastFocus();
        if (field && actionObject[field] !== undefined) {
            const currentValue = actionObject[field] || '';
            actionObject[field] = `${currentValue}$${placeholder}`;
        }
    }

    getFieldForLastFocus() {
        const focusMap = {
            'subject': 'subject',
            'body': 'body',
            'introduction': 'introduction',
            'message': 'message',
            'workOrderDescription': 'workOrderDescription',
            'assetId': 'assetId',
            'taskId': 'taskId',
            'customerId': 'customerId',
            'clientId': 'clientId',
            'clientSecret': 'clientSecret',
            'companyName': 'companyName',
            'subTypeId': 'subTypeId',
            'contactName': 'contactName',
            'contactAddress': 'contactAddress',
            'storeId': 'storeId',
            'assetTagId': 'assetTagId',
            'description': 'description'
        };
        return focusMap[this.lastActionFocus];
    }



    get readyToSave() {
        // Template form validation (if exists)
        if (this.templateForm && this.templateForm.$invalid) return false;

        // Acknowledgment duration validation
        if (this.invalidAcknowledgmentDuration) return false;

        // Template name is required
        if (!this.template || !this.template.displayName || this.template.displayName.trim() === '') return false;

        // Scope validation - always valid for ALL_PROJECTS
        if (!this.isValidScope()) return false;

        // Device validation - required for save
        if (!this.isValidDevices()) return false;

        // Trigger validation - required for save
        return this.isValidTrigger();


    }

    isValidScope() {
        // Ensure scope structure exists
        if (!this.template.scope) {
            this.template.scope = { type: 'ALL_PROJECTS', labels: {} };
        }

        switch (this.template.scope.type) {
            case 'ALL_PROJECTS':
                return true; // All projects is always valid
            case 'PROJECT_LABELS':
                // Keys must be non-empty and whitespace-free; the rules API rejects
                // label filters whose keys contain spaces
                return this.template.scope.labels &&
                    Object.keys(this.template.scope.labels).length > 0 &&
                    Object.keys(this.template.scope.labels).every(key => key.trim() !== '' && !/\s/.test(key));
            default:
                // Default to ALL_PROJECTS if unknown type
                this.template.scope.type = 'ALL_PROJECTS';
                return true;
        }
    }

    isValidDevices() {
        // If no device mode is selected, default to 'ALL' (all devices)
        if (!this.template.deviceMode) {
            this.template.deviceMode = 'ALL';
        }

        switch (this.template.deviceMode) {
            case 'ALL':
                return true; // All devices is always valid
            case 'LABELS':
                // Keys must be non-empty and whitespace-free; the rules API rejects
                // label filters whose keys contain spaces
                return this.template.deviceLabels &&
                    Object.keys(this.template.deviceLabels).length > 0 &&
                    Object.keys(this.template.deviceLabels).every(key => key.trim() !== '' && !/\s/.test(key));
            default:
                return true; // Default to valid for unknown modes
        }
    }

    isValidTrigger() {
        const field = this.template.trigger.field;
        if (!field) return false;

        const operator = this.getOperatorByField(field);

        switch (operator) {
            case 'range': {
                const range = this.template.trigger.range;
                if (!range) return false;

                if (range.type === 'ABOVE') {
                    return range.upper !== null && range.upper !== '';
                } else if (range.type === 'BELOW') {
                    return range.lower !== null && range.lower !== '';
                } else if (['OUTSIDE', 'WITHIN'].includes(range.type)) {
                    return range.upper !== null && range.upper !== '' &&
                        range.lower !== null && range.lower !== '';
                }
                return false;
            }
            case 'count':
                return this.template.trigger.triggerCount && this.template.trigger.triggerCount >= 2;
            case 'present':
            case 'contact':
            case 'touch':
            case 'motion':
            case 'occupancy':
            case 'connectionStatus':
            case 'batteryState':
                return true;
            default:
                return false;
        }
    }



    templatesAreEqual(templateA, templateB) { // eslint-disable-line class-methods-use-this
        return _isEqual(angular.toJson(templateA), angular.toJson(templateB));
    }

    get isEdited() {
        return !this.templatesAreEqual(this.originalTemplate, this.template);
    }

    prepareTemplateForSave() {
        return prepareForSave(this.template);
    }

    // Save/Cancel methods
    async createTemplate() {
        if (!this.readyToSave) return;

        try {
            const templateData = this.prepareTemplateForSave();
            await this.onCreate({ $event: templateData });
            // Success message is handled by parent component
        } catch (error) {
            // Error handling is done by parent component
            console.error('Failed to create template:', error); // eslint-disable-line no-console
        }
    }

    async updateTemplate() {
        if (!this.readyToSave) return;

        try {
            const templateData = this.prepareTemplateForSave();
            await this.onUpdate({ $event: templateData });
            // Update originalTemplate to reflect saved state (prevents button from staying active)
            this.originalTemplate = _cloneDeep(this.template);
            // Success message is handled by parent component
        } catch (error) {
            // Error handling is done by parent component
            console.error('Failed to update template:', error); // eslint-disable-line no-console
        }
    }

    deleteTemplate() {
        if (this.isNew) return;

        this.DialogService.confirm({
            title: 'Delete Template',
            textContent: `Are you sure you want to delete "${this.template.displayName}"?`,
            ok: 'Delete',
            cancel: 'Cancel'
        }).then(async () => {
            try {
                await this.onDelete({ $event: this.template.name || this.template.id });
                // Success message is handled by parent component
            } catch (error) {
                // Error handling is done by parent component
                console.error('Failed to delete template:', error); // eslint-disable-line no-console
            }
        });
    }

    cancel() {
        this.onCancel();
    }

    updateDynamicInputs() {
        // Update the available dynamic inputs based on the trigger type
        if (!this.template || !this.template.trigger || !this.template.trigger.field) {
            this.availableDynamicInputs = [];
            return;
        }

        this.availableDynamicInputs = getAvailableDynamicInputs(this.template.trigger.field, {
            reminderFeatureFlag: this.template.reminderNotifications,
        });
    }

    hasCorrigoAction() {
        return this.FeatureFlags.isActive("notifications_corrigo");
    }

    hasServiceChannelAction() {
        return this.FeatureFlags.isActive("notifications_service_channel");
    }

    hasJoltListAction() {
        return this.FeatureFlags.isActive("notifications_jolt_list");
    }

    // Default Corrigo work order description
    get defaultCorrigoWorkOrderDescription() {
        return `Created by a DT Sensor via API. DT Studio Dashboard: https://studio.disruptive-technologies.com/projects/PROJECT_ID/dashboard`;
    }

    // Contact Groups Methods
    getContactGroupDisplayName(resourceName) {
        const contactGroup = this.contactGroups ? this.contactGroups.find(cg => cg.name === resourceName) : null;
        return contactGroup ? contactGroup.displayName : resourceName;
    }

    getContactGroupDescription(resourceName) {
        const contactGroup = this.contactGroups ? this.contactGroups.find(cg => cg.name === resourceName) : null;
        return contactGroup ? contactGroup.description : '';
    }

    addContactGroupToAction(action, contactGroupName) {
        let actionObject;
        if (action.type === 'PHONE_CALL') {
            actionObject = action.phoneCall;
        } else {
            actionObject = action[action.type.toLowerCase()];
        }
        if (!actionObject.contactGroups) {
            actionObject.contactGroups = [];
        }
        if (!actionObject.contactGroups.includes(contactGroupName)) {
            actionObject.contactGroups.push(contactGroupName);
            this.recomputeAllWarnings();
        }
    }

    removeContactGroupFromAction(action, contactGroupName) {
        const actionObject = action[action.type.toLowerCase()];
        if (actionObject.contactGroups) {
            const index = actionObject.contactGroups.indexOf(contactGroupName);
            if (index > -1) {
                actionObject.contactGroups.splice(index, 1);
                this.recomputeAllWarnings();
            }
        }
    }

    getContactGroupWarnings(contactGroupResourceName, actionType) {
        if (!this.computedWarnings) return [];
        const groupWarnings = this.computedWarnings[contactGroupResourceName];
        if (!groupWarnings) return [];
        return groupWarnings[actionType] || [];
    }

    recomputeAllWarnings() {
        this.computedWarnings = {};
        if (!this.template || !this.template.escalationLevels) return;

        const actionKeys = { 'SMS': 'sms', 'PHONE_CALL': 'phoneCall', 'EMAIL': 'email' };

        this.template.escalationLevels.forEach(level => {
            if (!level.actions) return;
            level.actions.forEach(action => {
                Object.keys(actionKeys).forEach(type => {
                    const actionObj = action[actionKeys[type]];
                    if (!actionObj || !actionObj.contactGroups) return;
                    actionObj.contactGroups.forEach(cgName => {
                        if (!this.computedWarnings[cgName]) {
                            this.computedWarnings[cgName] = {};
                        }
                        if (!this.computedWarnings[cgName][type]) {
                            this.computedWarnings[cgName][type] = this.computeWarningsFor(cgName, type);
                        }
                    });
                });
            });
        });
    }

    computeWarningsFor(contactGroupResourceName, actionType) {
        if (!this.contactGroupInfo) return [];

        // If Map doesn't have this group yet, trigger a fetch
        if (this.contactGroupInfo instanceof Map && !this.contactGroupInfo.has(contactGroupResourceName)) {
            this.fetchContactGroupInfoIfNeeded(contactGroupResourceName);
            return [];
        }

        const info = this.contactGroupInfo instanceof Map
            ? this.contactGroupInfo.get(contactGroupResourceName)
            : null;
        if (!info) return [];

        const warnings = [];
        const scopeProjects = this.getProjectsInScope();
        const totalProjects = scopeProjects.length;

        if (info.totalContacts === 0) {
            warnings.push({
                message: 'This contact group has no contacts',
                missingProjects: scopeProjects.map(p => ({
                    name: p.name,
                    displayName: p.displayName || p.name,
                    inlineName: '', inlineEmail: '', inlinePhone: ''
                })),
                totalProjects
            });
            return warnings;
        }

        // Per-project check
        if (info.byProject && totalProjects > 0) {
            const projectsMissingContacts = [];
            const projectsMissingType = [];

            scopeProjects.forEach(project => {
                const projectInfo = info.byProject.get(project.name);
                const entry = {
                    name: project.name,
                    displayName: project.displayName || project.name,
                    inlineName: '', inlineEmail: '', inlinePhone: ''
                };
                if (!projectInfo) {
                    projectsMissingContacts.push(entry);
                } else if ((actionType === 'SMS' || actionType === 'PHONE_CALL') && !projectInfo.hasPhones) {
                    projectsMissingType.push(entry);
                } else if (actionType === 'EMAIL' && !projectInfo.hasEmails) {
                    projectsMissingType.push(entry);
                }
            });

            if (projectsMissingContacts.length > 0) {
                warnings.push({
                    message: `No contacts in ${projectsMissingContacts.length} of ${totalProjects} projects`,
                    missingProjects: projectsMissingContacts,
                    totalProjects
                });
            }

            const typeLabel = actionType === 'EMAIL' ? 'email addresses' : 'phone numbers';
            if (projectsMissingType.length > 0) {
                warnings.push({
                    message: `No ${typeLabel} in ${projectsMissingType.length} of ${totalProjects} projects`,
                    missingProjects: projectsMissingType,
                    totalProjects
                });
            }
        } else {
            // Global fallback checks when no project info available
            if ((actionType === 'SMS' || actionType === 'PHONE_CALL') && !info.hasPhones) {
                warnings.push({ message: 'No contacts in this group have phone numbers', missingProjects: [], totalProjects: 0 });
            }
            if (actionType === 'EMAIL' && !info.hasEmails) {
                warnings.push({ message: 'No contacts in this group have email addresses', missingProjects: [], totalProjects: 0 });
            }
        }

        return warnings;
    }

    clearWarningsCache() {
        this.recomputeAllWarnings();
    }

    getProjectsInScope() {
        if (!this.template.scope || this.template.scope.type === 'ALL_PROJECTS') {
            return this.allProjects || [];
        }
        if (this.template.scope.type === 'PROJECT_LABELS' && this.matchingProjects) {
            return this.matchingProjects;
        }
        return this.allProjects || [];
    }

    toggleWarningExpand(contactGroupResourceName, warningIndex) {
        const key = `${contactGroupResourceName}_${warningIndex}`;
        if (!this.expandedWarnings) {
            this.expandedWarnings = new Set();
        }
        if (this.expandedWarnings.has(key)) {
            this.expandedWarnings.delete(key);
        } else {
            this.expandedWarnings.add(key);
        }
    }

    isWarningExpanded(contactGroupResourceName, warningIndex) {
        if (!this.expandedWarnings) return false;
        return this.expandedWarnings.has(`${contactGroupResourceName}_${warningIndex}`);
    }

    async addContactToProject(contactGroupResourceName, project, actionType) {
        const displayName = (project.inlineName || '').trim();
        const email = actionType === 'EMAIL' ? (project.inlineEmail || '').trim() : null;
        const phone = (actionType === 'SMS' || actionType === 'PHONE_CALL') ? (project.inlinePhone || '').trim() : null;

        // Validation — same rules as contact group form
        if (!displayName) {
            this.ToastService.showSimpleText('Please enter a contact name');
            return;
        }
        if (!email && !phone) {
            this.ToastService.showSimpleText('Please provide either an email or phone number');
            return;
        }
        if (phone && !phone.startsWith('+')) {
            this.ToastService.showSimpleText('Phone number must start with "+" followed by country code e.g., +15551234567');
            return;
        }

        const projectId = project.name.split('/')[1];

        try {
            project.saving = true;
            this.$scope.$applyAsync();

            await this.IAMService.createContact(projectId, {
                displayName,
                email: email || null,
                phoneNumber: phone || null,
                contactGroup: contactGroupResourceName
            });

            project.saved = true;
            this.ToastService.showSimpleText(`Contact added to ${project.displayName}`);

            // Clear warnings cache and re-fetch contact group info
            this.clearWarningsCache();
            if (this.pendingContactGroupFetches) {
                this.pendingContactGroupFetches.delete(contactGroupResourceName);
            }
            await this.fetchContactGroupInfoIfNeeded(contactGroupResourceName);

            this.$scope.$applyAsync();
        } catch (error) {
            console.error('Failed to add contact:', error); // eslint-disable-line no-console
            this.ToastService.showSimpleText(`Failed to add contact: ${error.data?.error || error.message || 'Unknown error'}`);
        } finally {
            project.saving = false;
            this.$scope.$applyAsync();
        }
    }

    async saveAllContacts(contactGroupResourceName, missingProjects, actionType) {
        // Filter to only rows that have data filled in
        const projectsToSave = missingProjects.filter(project => {
            if (project.saved || project.saving) return false;
            const hasName = project.inlineName && project.inlineName.trim();
            const hasEmail = project.inlineEmail && project.inlineEmail.trim();
            const hasPhone = project.inlinePhone && project.inlinePhone.trim();
            if (!hasName) return false;
            if (actionType === 'EMAIL') return hasEmail;
            return hasPhone;
        });

        if (projectsToSave.length === 0) {
            this.ToastService.showSimpleText(`No rows to save. Fill in Name and ${actionType === 'EMAIL' ? 'Email' : 'Phone Number'} to add contacts.`);
            return;
        }

        // Validate phone format for all rows before saving
        if (actionType === 'SMS' || actionType === 'PHONE_CALL') {
            const invalidPhone = projectsToSave.find(p => !p.inlinePhone.trim().startsWith('+'));
            if (invalidPhone) {
                this.ToastService.showSimpleText(`Phone number for "${invalidPhone.displayName}" must start with "+" followed by country code`);
                return;
            }
        }

        // Find the warning object that owns these missingProjects so we can set savingAll on it
        const warnings = this.getContactGroupWarnings(contactGroupResourceName, actionType);
        const warning = warnings.find(w => w.missingProjects === missingProjects);

        // Mark all as saving
        if (warning) warning.savingAll = true;
        projectsToSave.forEach(p => { p.saving = true; });
        this.$scope.$applyAsync();

        const results = await this.saveContactsBatched(projectsToSave, contactGroupResourceName, actionType);
        const { savedCount, failedCount } = results;

        if (warning) warning.savingAll = false;

        // Show summary
        if (failedCount === 0) {
            this.ToastService.showSimpleText(`${savedCount} contact${savedCount > 1 ? 's' : ''} added successfully`);
        } else {
            this.ToastService.showSimpleText(`${savedCount} added, ${failedCount} failed`);
        }

        // Refresh contact group info
        this.clearWarningsCache();
        if (this.pendingContactGroupFetches) {
            this.pendingContactGroupFetches.delete(contactGroupResourceName);
        }
        await this.fetchContactGroupInfoIfNeeded(contactGroupResourceName);
        this.$scope.$applyAsync();
    }

    saveContact(project, contactGroupResourceName, actionType) {
        const projectId = project.name.split('/')[1];
        const displayName = project.inlineName.trim();
        const email = actionType === 'EMAIL' ? project.inlineEmail.trim() : null;
        const phone = (actionType === 'SMS' || actionType === 'PHONE_CALL') ? project.inlinePhone.trim() : null;

        return this.IAMService.createContact(projectId, {
            displayName,
            email: email || null,
            phoneNumber: phone || null,
            contactGroup: contactGroupResourceName
        }).then(() => {
            project.saved = true;
        }).catch((error) => {
            console.error(`Failed to add contact to ${project.displayName}:`, error); // eslint-disable-line no-console
            project.saving = false;
            project.error = error.data?.error || error.message || 'Failed';
        });
    }

    async saveContactsBatched(projectsToSave, contactGroupResourceName, actionType) {
        const BATCH_SIZE = 5;

        for (let i = 0; i < projectsToSave.length; i += BATCH_SIZE) {
            const batch = projectsToSave.slice(i, i + BATCH_SIZE);
            // eslint-disable-next-line no-await-in-loop
            await Promise.all(batch.map(p => this.saveContact(p, contactGroupResourceName, actionType)));
            this.$scope.$applyAsync();
        }

        const savedCount = projectsToSave.filter(p => p.saved).length;
        const failedCount = projectsToSave.filter(p => p.error).length;
        return { savedCount, failedCount };
    }

    async fetchContactGroupInfoIfNeeded(contactGroupResourceName) {
        // Prevent duplicate fetches
        if (!this.pendingContactGroupFetches) {
            this.pendingContactGroupFetches = new Set();
        }
        if (this.pendingContactGroupFetches.has(contactGroupResourceName)) {
            return;
        }
        this.pendingContactGroupFetches.add(contactGroupResourceName);

        try {
            const organizationName = this.ProjectManager.currentProject.organization;
            const response = await this.IAMService.listContactsForOrganization({
                organization: organizationName,
                contactGroup: contactGroupResourceName
            });
            const contacts = response.contacts || [];
            this.contactGroupInfo = new Map(this.contactGroupInfo);
            this.contactGroupInfo.set(contactGroupResourceName, buildContactGroupInfo(contacts));
            this.recomputeAllWarnings();
            this.$scope.$applyAsync();
        } catch (error) {
            console.error(`Failed to fetch contact info for ${contactGroupResourceName}:`, error); // eslint-disable-line no-console
        } finally {
            this.pendingContactGroupFetches.delete(contactGroupResourceName);
        }
    }

    fetchMatchingProjectsCount() {
        this.isLoadingProjectsCount = true;
        this.matchingProjectsCount = 0;
        this.totalProjectsCount = 0;
        this.matchingProjects = [];
        this.nonMatchingProjects = [];
        this.hasProjectLabels = false;

        // Always fetch total projects count for organization templates
        this.fetchAllProjects().then(() => {
            if (this.template.scope.type !== 'PROJECT_LABELS' || !this.template.scope.labels || Object.keys(this.template.scope.labels).length === 0) {
                this.isLoadingProjectsCount = false;
                this.recomputeAllWarnings();
                this.checkProjectsWithoutTimezone();
                return;
            }

            this.hasProjectLabels = true;
            this.fetchMatchingProjects();
        });
    }

    fetchAllProjects() {
        const fetchPages = (pageToken = null, accumulatedProjects = []) => {
            const params = {
                organization: this.ProjectManager.currentProject.organization
            };
            if (pageToken) {
                params.pageToken = pageToken;
            }

            return this.IAMService.projects(params)
                .then(response => {
                    const allProjects = accumulatedProjects.concat(response.data);
                    if (response.nextPageToken) {
                        return fetchPages(response.nextPageToken, allProjects);
                    }
                    return allProjects;
                });
        };

        return fetchPages()
            .then(allProjects => {
                this.totalProjectsCount = allProjects.length;
                // Store all projects for later use in determining non-matching projects
                this.allProjects = allProjects;
            })
            .catch(error => {
                console.error('Error fetching all projects:', error); // eslint-disable-line no-console
            });
    }

    fetchMatchingProjects() {
        const labelFilters = Object.keys(this.template.scope.labels).map(key => {
            const value = this.template.scope.labels[key];
            if (value === '') {
                return key;
            }
            return `${key}=${value}`;
        });

        const fetchPages = (pageToken = null, accumulatedProjects = []) => {
            const params = {
                labelFilters: labelFilters,
                organization: this.ProjectManager.currentProject.organization
            };
            if (pageToken) {
                params.pageToken = pageToken;
            }

            return this.IAMService.projects(params)
                .then(response => {
                    const matchingProjects = accumulatedProjects.concat(response.data);
                    if (response.nextPageToken) {
                        return fetchPages(response.nextPageToken, matchingProjects);
                    }
                    return matchingProjects;
                });
        };

        fetchPages()
            .then(matchingProjects => {
                this.matchingProjects = matchingProjects;
                this.matchingProjectsCount = matchingProjects.length;

                // Calculate non-matching projects
                const matchingProjectIds = new Set(matchingProjects.map(p => p.name));
                this.nonMatchingProjects = this.allProjects.filter(project => !matchingProjectIds.has(project.name));

                this.isLoadingProjectsCount = false;
                this.recomputeAllWarnings();
                this.checkProjectsWithoutTimezone();
            })
            .catch(error => {
                console.error('Error fetching matching projects:', error); // eslint-disable-line no-console
                this.isLoadingProjectsCount = false;
            });
    }

    toggleProjectDetails() {
        this.showProjectDetails = !this.showProjectDetails;
    }

    openProjectInNewTab(projectName) {
        const projectId = projectName.split('/')[1]; // "projects/{projectId}"
        if (projectId) {
            const sensorsUrl = this.$state.href(States.PROJECT_SETTINGS, { projectId: projectId }, { absolute: true });
            // Open in new tab
            window.open(sensorsUrl, '_blank');
        }
    }
}
